import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../../../modules/auth/core/_requests';
import { PageTitle } from '../../../../_designereyes/layout/core';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { FileUpload, FileUploadUploadEvent } from 'primereact/fileupload';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Checkbox } from 'primereact/checkbox';
import { ProgressBar } from 'primereact/progressbar';
import { Card } from 'primereact/card';
import { TabView, TabPanel } from 'primereact/tabview';
import { Fieldset } from 'primereact/fieldset';
import { RadioButton } from 'primereact/radiobutton';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Panel } from 'primereact/panel';
import { Badge } from 'primereact/badge';
import { Divider } from 'primereact/divider';
import { io, Socket } from 'socket.io-client';
import * as XLSX from 'xlsx';
import 'primereact/resources/themes/arya-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

// Types
interface ExcelRow {
  [key: string]: any;
}

interface ColumnMapping {
  sourceColumn: string;
  stagingHeader?: string;
  catalogHeader?: string;
  transformationTable?: string;
}

interface VendorOption {
  label: string;
  value: string;
  intVendor: number;
}

interface UnmappedValue {
  table: string;
  value: string;
  standardizedValue: string;
  mainCategory?: string;
}

interface ProgressStep {
  id: string;
  text: string;
  status: 'pending' | 'completed' | 'error';
}

export const CatalogUploader: React.FC = () => {
  // State management
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [excelData, setExcelData] = useState<ExcelRow[]>([]);
  const [excelHeaders, setExcelHeaders] = useState<string[]>([]);
  const [headerRow, setHeaderRow] = useState<number>(2);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [columnMappings, setColumnMappings] = useState<Map<string, ColumnMapping>>(new Map());
  const [availableMappings, setAvailableMappings] = useState<string[]>([]);
  const [vendors, setVendors] = useState<VendorOption[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<VendorOption | null>(null);
  const [vendorSelectionMode, setVendorSelectionMode] = useState<'dropdown' | 'data'>('dropdown');
  const [uploadToProd, setUploadToProd] = useState<boolean>(true);
  const [uploadToRaw, setUploadToRaw] = useState<boolean>(true);
  const [processing, setProcessing] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [progressSteps, setProgressSteps] = useState<ProgressStep[]>([]);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [unmappedValues, setUnmappedValues] = useState<UnmappedValue[]>([]);
  const [showUnmappedDialog, setShowUnmappedDialog] = useState<boolean>(false);
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);

  const toast = useRef<Toast>(null);
  const fileUploadRef = useRef<FileUpload>(null);

  // Socket.io setup
  useEffect(() => {
    const newSocket = io(API_URL, { transports: ['websocket'] });
    setSocket(newSocket);

    newSocket.on('connect', () => {
      console.log('Connected to server');
    });

    newSocket.on('catalogUploadProgress', (data: { message: string; progress: number }) => {
      addProgressStep(data.message, 'pending');
      setProgress(data.progress);
    });

    newSocket.on('catalogUploadComplete', (data: { message: string; success: boolean }) => {
      addProgressStep(data.message, data.success ? 'completed' : 'error');
      setProcessing(false);
      setProgress(100);
      
      if (data.success) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Catalog uploaded successfully',
          life: 5000
        });
      }
    });

    newSocket.on('catalogUploadError', (data: { message: string }) => {
      addProgressStep(data.message, 'error');
      setProcessing(false);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: data.message,
        life: 5000
      });
    });

    newSocket.on('unmappedValuesFound', (data: { unmappedValues: UnmappedValue[] }) => {
      setUnmappedValues(data.unmappedValues);
      setShowUnmappedDialog(true);
    });

    return () => {
      newSocket.disconnect();
    };
  }, []);

  // Load initial data
  useEffect(() => {
    loadVendors();
    loadAvailableMappings();
  }, []);

  const addProgressStep = (text: string, status: 'pending' | 'completed' | 'error') => {
    const newStep: ProgressStep = {
      id: `${Date.now()}-${Math.random()}`,
      text,
      status
    };
    setProgressSteps(prev => [...prev, newStep]);
  };

  const loadVendors = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/catalog-uploader/vendors`);
      setVendors(response.data.vendors);
    } catch (error) {
      console.error('Failed to load vendors:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load vendors',
        life: 3000
      });
    }
  };

  const loadAvailableMappings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/catalog-uploader/mappings`);
      setAvailableMappings(response.data.mappings || []);
    } catch (error) {
      console.error('Failed to load mappings:', error);
      // Add fallback mappings
      const fallbackMappings = [
        'upc', 'brand', 'model', 'color', 'size', 'price', 'cost', 'category',
        'vendor_name', 'brand_code', 'color_code', 'size_code', 'product_type',
        'description', 'sku', 'asin', 'collection', 'gender', 'material'
      ];
      setAvailableMappings(fallbackMappings);
    }
  };

  const handleFileUpload = useCallback((event: FileUploadUploadEvent) => {
    const file = event.files[0];
    setExcelFile(file);
    processExcelFile(file);
  }, [headerRow]);

  const handleFileSelect = useCallback((event: any) => {
    const file = event.files[0];
    if (file) {
      setExcelFile(file);
      processExcelFile(file);
    }
  }, [headerRow]);

  const processExcelFile = (file: File) => {
    console.log('Processing Excel file:', file.name);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convert to JSON with header row
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
          header: 1,
          raw: false,
          defval: null
        }) as any[][];

        console.log('Excel data loaded:', jsonData.length, 'rows');

        if (jsonData.length > headerRow - 1) {
          const headers = jsonData[headerRow - 1] as string[];
          const dataRows = jsonData.slice(headerRow).map(row => {
            const rowObj: ExcelRow = {};
            headers.forEach((header, index) => {
              rowObj[header] = row[index] || null;
            });
            return rowObj;
          });

          console.log('Setting headers:', headers);
          console.log('Setting data rows:', dataRows.length);

          setExcelHeaders(headers);
          setExcelData(dataRows);
          loadColumnMappings(headers);
          setActiveTabIndex(1); // Auto-advance to column mapping
          
          toast.current?.show({
            severity: 'success',
            summary: 'File Processed Successfully',
            detail: `Loaded ${dataRows.length} rows with ${headers.length} columns`,
            life: 4000
          });
        } else {
          throw new Error(`Invalid header row number. File has ${jsonData.length} rows, but header row is set to ${headerRow}`);
        }
      } catch (error) {
        console.error('Error processing Excel file:', error);
        toast.current?.show({
          severity: 'error',
          summary: 'Processing Error',
          detail: `Failed to process Excel file: ${error instanceof Error ? error.message : 'Unknown error'}`,
          life: 5000
        });
      }
    };
    
    reader.onerror = (error) => {
      console.error('FileReader error:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'File Read Error',
        detail: 'Failed to read Excel file',
        life: 3000
      });
    };
    
    reader.readAsBinaryString(file);
  };

  const loadColumnMappings = async (headers: string[]) => {
    try {
      const response = await axios.post(`${API_URL}/api/catalog-uploader/column-mappings`, {
        headers
      });
      
      const mappings = new Map<string, ColumnMapping>();
      response.data.mappings.forEach((mapping: any) => {
        mappings.set(mapping.sourceColumn, mapping);
      });
      setColumnMappings(mappings);
    } catch (error) {
      console.error('Failed to load column mappings:', error);
    }
  };

  const handleColumnSelection = (columnName: string, selected: boolean) => {
    if (selected) {
      setSelectedColumns(prev => [...prev, columnName]);
    } else {
      setSelectedColumns(prev => prev.filter(col => col !== columnName));
    }
  };

  const handleMappingChange = (columnName: string, newMapping: string) => {
    const currentMapping = columnMappings.get(columnName) || { sourceColumn: columnName };
    setColumnMappings(prev => new Map(prev.set(columnName, {
      ...currentMapping,
      catalogHeader: newMapping
    })));
  };

  const validateUpload = (): string | null => {
    if (!excelFile) return 'Please select an Excel file';
    if (selectedColumns.length === 0) return 'Please select at least one column';
    if (vendorSelectionMode === 'dropdown' && !selectedVendor) return 'Please select a vendor';
    if (!uploadToProd && !uploadToRaw) return 'Please select at least one upload target';
    return null;
  };

  const handleUpload = async () => {
    const validationError = validateUpload();
    if (validationError) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation Error',
        detail: validationError,
        life: 3000
      });
      return;
    }

    setProcessing(true);
    setProgress(0);
    setProgressSteps([]);

    const formData = new FormData();
    formData.append('file', excelFile!);
    formData.append('headerRow', headerRow.toString());
    formData.append('selectedColumns', JSON.stringify(selectedColumns));
    formData.append('columnMappings', JSON.stringify(Array.from(columnMappings.entries())));
    formData.append('vendorSelectionMode', vendorSelectionMode);
    if (selectedVendor) {
      formData.append('selectedVendor', JSON.stringify(selectedVendor));
    }
    formData.append('uploadToProd', uploadToProd.toString());
    formData.append('uploadToRaw', uploadToRaw.toString());
    formData.append('socketId', socket?.id || '');

    try {
      await axios.post(`${API_URL}/api/catalog-uploader/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    } catch (error: any) {
      setProcessing(false);
      console.error('Upload failed:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Upload Failed',
        detail: error.response?.data?.message || 'Unknown error occurred',
        life: 5000
      });
    }
  };

  const handleUnmappedValuesSubmit = async () => {
    try {
      await axios.post(`${API_URL}/api/catalog-uploader/resolve-unmapped`, {
        unmappedValues,
        socketId: socket?.id
      });
      setShowUnmappedDialog(false);
    } catch (error) {
      console.error('Failed to resolve unmapped values:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to resolve unmapped values',
        life: 3000
      });
    }
  };

  // Custom file upload template
  const customFileUploadHeader = () => (
    <div className="flex align-items-center justify-content-between w-full">
      <div className="flex align-items-center">
        <i className="pi pi-cloud-upload text-4xl text-primary mr-3"></i>
                 <div>
           <h3 className="m-0 text-color">Upload Excel File</h3>
           <p className="m-0 text-color-secondary">Select your catalog Excel file to begin processing</p>
         </div>
      </div>
      <Badge value={excelFile ? '1 file selected' : '0 files'} severity={excelFile ? 'success' : 'secondary'} />
    </div>
  );

  // Render column selection table with improved UI
  const renderColumnTable = () => (
    <div className="column-mapping-container" style={{ height: '60vh', overflowY: 'auto' }}>
      <DataTable 
        value={excelHeaders.map(header => ({ 
          column: header, 
          selected: selectedColumns.includes(header),
          mapping: columnMappings.get(header)?.catalogHeader || ''
        }))}
        scrollable
        scrollHeight="flex"
        className="modern-datatable"
        paginator={false}
        rows={50}
        emptyMessage="No columns found. Please upload and process an Excel file first."
      >
        <Column 
          field="selected" 
          header="Select" 
          style={{ width: '80px' }}
          body={(rowData) => (
            <Checkbox
              checked={rowData.selected}
              onChange={(e) => handleColumnSelection(rowData.column, e.checked!)}
              className="p-checkbox-lg"
            />
          )}
        />
        <Column 
          field="column" 
          header="Excel Column" 
          style={{ minWidth: '250px' }}
          body={(rowData) => (
            <div className="flex align-items-center">
              <i className="pi pi-table text-primary mr-2"></i>
              <span className="font-semibold">{rowData.column}</span>
            </div>
          )}
        />
        <Column 
          header="Database Field"
          style={{ minWidth: '300px' }}
          body={(rowData) => (
            <Dropdown
              value={columnMappings.get(rowData.column)?.catalogHeader || ''}
              options={[
                { label: 'Not Mapped', value: '' },
                ...availableMappings.map(m => ({ label: m, value: m }))
              ]}
              onChange={(e) => handleMappingChange(rowData.column, e.value)}
              placeholder="Select database field"
              className="w-full modern-dropdown"
              filter
              showClear
            />
          )}
        />
      </DataTable>
    </div>
  );

  // Render Excel preview with improved styling
  const renderExcelPreview = () => {
    if (!excelData.length) return null;

    const previewData = excelData.slice(0, 5); // Show first 5 rows
    const columns = excelHeaders.slice(0, 10).map(header => ({ // Show first 10 columns
      field: header,
      header: header
    }));

    return (
      <div className="excel-preview" style={{ height: '300px', overflowY: 'auto' }}>
        <DataTable 
          value={previewData}
          scrollable
          scrollHeight="flex"
          className="modern-datatable-preview"
        >
          {columns.map(col => (
            <Column 
              key={col.field} 
              field={col.field} 
              header={col.header}
              style={{ minWidth: '150px' }}
            />
          ))}
        </DataTable>
      </div>
    );
  };

  // Render progress steps with better styling
  const renderProgressSteps = () => (
    <div className="progress-steps-container mt-4">
      {progressSteps.map(step => (
        <div key={step.id} className="flex align-items-center mb-3 p-2 border-round surface-card progress-step">
          <i className={`pi ${
            step.status === 'completed' ? 'pi-check-circle text-green-400' :
            step.status === 'error' ? 'pi-times-circle text-red-400' :
            'pi-clock text-blue-400'
          } mr-3 text-xl`} />
          <span className={`${step.status === 'error' ? 'text-red-400' : 'text-color'} flex-1`}>
            {step.text}
          </span>
          {step.status === 'pending' && <i className="pi pi-spin pi-spinner text-blue-400"></i>}
        </div>
      ))}
    </div>
  );

  return (
    <div className="catalog-uploader-container">
      <PageTitle breadcrumbs={[]}>
        <div className="flex align-items-center">
          <i className="pi pi-upload text-primary mr-2 text-2xl"></i>
          Catalog Uploader
        </div>
      </PageTitle>
      <Toast ref={toast} />

      <style>{`
        .catalog-uploader-container {
          min-height: calc(100vh - 200px);
          padding: 1rem;
        }
        
        .info-card {
          transition: transform 0.2s ease;
        }
        
        .info-card:hover {
          transform: translateY(-2px);
        }
        
        .vendor-selection-card {
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .vendor-selection-card:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
        
        .progress-step {
          transition: all 0.3s ease;
        }
        
        .modern-datatable .p-datatable-thead > tr > th {
          font-weight: 600;
          letter-spacing: 0.5px;
        }
        
        .upload-zone {
          border: 2px dashed #6c757d;
          border-radius: 8px;
          transition: border-color 0.2s ease;
        }
        
        .upload-zone:hover {
          border-color: var(--primary-color);
        }
      `}</style>

      <div className="grid h-full">
        <div className="col-12 h-full">
          <TabView 
            activeIndex={activeTabIndex} 
            onTabChange={(e) => setActiveTabIndex(e.index)}
            className="h-full"
          >
            {/* File Upload Tab */}
            <TabPanel 
              header={
                <div className="flex align-items-center">
                  <i className="pi pi-upload mr-2"></i>
                  <span>1. File Upload</span>
                  {excelFile && <Badge value="✓" severity="success" className="ml-2" />}
                </div>
              }
            >
              <Card className="h-full">
                <div className="grid">
                  <div className="col-12 lg:col-8">
                    <Panel header={customFileUploadHeader()} className="mb-4">
                      <div className="field mb-4">
                                                 <label htmlFor="headerRow" className="block font-semibold mb-2 text-color">
                           <i className="pi pi-arrow-down text-primary mr-2"></i>
                           Header Row Number:
                         </label>
                                                 <InputNumber
                           id="headerRow"
                           value={headerRow}
                           onValueChange={(e) => setHeaderRow(e.value || 1)}
                           min={1}
                           className="w-full"
                         />
                                                 <small className="text-color-secondary">Specify which row contains the column headers</small>
                      </div>
                      
                      <FileUpload
                        ref={fileUploadRef}
                        name="file"
                        accept=".xlsx,.xls"
                        maxFileSize={50000000}
                        onSelect={handleFileSelect}
                        onUpload={handleFileUpload}
                        auto={false}
                        chooseLabel="Select Excel File"
                        uploadLabel="Process File"
                        cancelLabel="Remove"
                        className="modern-fileupload"
                                                 emptyTemplate={
                           <div className="text-center p-6 upload-zone">
                             <i className="pi pi-cloud-upload text-6xl text-color-secondary mb-3"></i>
                             <p className="text-color mb-0">Drag and drop files here or click to browse</p>
                             <small className="text-color-secondary">Maximum file size: 50MB</small>
                           </div>
                         }
                      />
                    </Panel>

                    {excelFile && (
                      <Panel header="File Information" className="mb-4">
                                                 <div className="grid">
                           <div className="col-12 md:col-4">
                             <div className="text-center p-3 info-card surface-card border-round">
                               <i className="pi pi-file-excel text-primary text-3xl mb-2"></i>
                               <p className="m-0 font-semibold text-primary">{excelFile.name}</p>
                             </div>
                           </div>
                           <div className="col-12 md:col-4">
                             <div className="text-center p-3 info-card surface-card border-round">
                               <i className="pi pi-list text-green-400 text-3xl mb-2"></i>
                               <p className="m-0 font-semibold">{excelHeaders.length}</p>
                               <p className="m-0 text-color-secondary">Headers Found</p>
                             </div>
                           </div>
                           <div className="col-12 md:col-4">
                             <div className="text-center p-3 info-card surface-card border-round">
                               <i className="pi pi-database text-blue-400 text-3xl mb-2"></i>
                               <p className="m-0 font-semibold">{excelData.length.toLocaleString()}</p>
                               <p className="m-0 text-color-secondary">Data Rows</p>
                             </div>
                           </div>
                         </div>
                      </Panel>
                    )}
                  </div>
                  
                  <div className="col-12 lg:col-4">
                    {excelData.length > 0 && (
                      <Panel header="Excel Preview" className="h-full">
                        {renderExcelPreview()}
                      </Panel>
                    )}
                  </div>
                </div>
              </Card>
            </TabPanel>

            {/* Column Mapping Tab */}
            <TabPanel 
              header={
                <div className="flex align-items-center">
                  <i className="pi pi-sitemap mr-2"></i>
                  <span>2. Column Mapping</span>
                  {selectedColumns.length > 0 && <Badge value={selectedColumns.length} severity="info" className="ml-2" />}
                </div>
              } 
              disabled={!excelData.length}
            >
              <Card title="Column Selection and Mapping" className="h-full">
                                 <div className="mb-4 p-3 surface-100 border-round border-left-3 border-primary">
                   <div className="flex align-items-center">
                     <i className="pi pi-info-circle text-primary mr-2"></i>
                     <span className="text-color">
                       Select the columns you want to include and map them to database fields
                     </span>
                   </div>
                 </div>
                {renderColumnTable()}
                <Divider />
                <div className="flex justify-content-between align-items-center">
                  <div className="flex align-items-center">
                    <Badge value={`${selectedColumns.length} columns selected`} severity="info" />
                    <Badge value={`${availableMappings.length} mappings available`} severity="secondary" className="ml-2" />
                  </div>
                  <Button 
                    label="Next: Vendor Selection" 
                    icon="pi pi-arrow-right" 
                    iconPos="right"
                    disabled={selectedColumns.length === 0}
                    onClick={() => setActiveTabIndex(2)}
                  />
                </div>
              </Card>
            </TabPanel>

            {/* Vendor Selection Tab */}
            <TabPanel 
              header={
                <div className="flex align-items-center">
                  <i className="pi pi-building mr-2"></i>
                  <span>3. Vendor Selection</span>
                  {selectedVendor && <Badge value="✓" severity="success" className="ml-2" />}
                </div>
              } 
              disabled={selectedColumns.length === 0}
            >
              <Card title="Vendor Configuration" className="h-full">
                <div className="grid">
                  <div className="col-12 lg:col-6">
                    <Panel header="Vendor Selection Method" className="mb-4">
                                             <div className="field">
                         <div className="flex align-items-center mb-4 p-3 border-round vendor-selection-card surface-card border-1 surface-border">
                           <RadioButton
                             inputId="vendor-dropdown"
                             name="vendorMode"
                             value="dropdown"
                             onChange={(e) => setVendorSelectionMode(e.value)}
                             checked={vendorSelectionMode === 'dropdown'}
                           />
                           <label htmlFor="vendor-dropdown" className="ml-3 flex-1">
                             <div className="font-semibold text-color">Select from dropdown</div>
                             <div className="text-color-secondary text-sm">Choose a vendor from the predefined list</div>
                           </label>
                           <i className="pi pi-list text-2xl text-color-secondary"></i>
                         </div>
                         
                         <div className="flex align-items-center p-3 border-round vendor-selection-card surface-card border-1 surface-border">
                           <RadioButton
                             inputId="vendor-data"
                             name="vendorMode"
                             value="data"
                             onChange={(e) => setVendorSelectionMode(e.value)}
                             checked={vendorSelectionMode === 'data'}
                           />
                           <label htmlFor="vendor-data" className="ml-3 flex-1">
                             <div className="font-semibold text-color">Use vendor from data</div>
                             <div className="text-color-secondary text-sm">Extract vendor information from the Excel data</div>
                           </label>
                           <i className="pi pi-table text-2xl text-color-secondary"></i>
                         </div>
                       </div>

                      {vendorSelectionMode === 'dropdown' && (
                        <div className="field mt-4">
                                                     <label htmlFor="vendor" className="block font-semibold mb-2 text-color">
                             <i className="pi pi-building text-primary mr-2"></i>
                             Select Vendor:
                           </label>
                                                     <Dropdown
                             id="vendor"
                             value={selectedVendor}
                             options={vendors}
                             onChange={(e) => setSelectedVendor(e.value)}
                             placeholder="Choose vendor"
                             className="w-full"
                             filter
                           />
                        </div>
                      )}
                    </Panel>
                  </div>
                  
                  <div className="col-12 lg:col-6">
                    <Panel header="Selected Vendor Information" className="mb-4">
                                             {selectedVendor ? (
                         <div className="text-center p-4">
                           <i className="pi pi-check-circle text-green-400 text-4xl mb-3"></i>
                           <h4 className="m-0 mb-2 text-color">{selectedVendor.label}</h4>
                           <Badge value={`ID: ${selectedVendor.intVendor}`} severity="success" />
                         </div>
                       ) : vendorSelectionMode === 'data' ? (
                         <div className="text-center p-4">
                           <i className="pi pi-table text-blue-400 text-4xl mb-3"></i>
                           <p className="text-color-secondary m-0">Vendor will be extracted from your data</p>
                         </div>
                       ) : (
                         <div className="text-center p-4">
                           <i className="pi pi-exclamation-triangle text-orange-400 text-4xl mb-3"></i>
                           <p className="text-color-secondary m-0">Please select a vendor to continue</p>
                         </div>
                       )}
                    </Panel>
                  </div>
                </div>
                
                <Divider />
                <div className="flex justify-content-between">
                  <Button 
                    label="Back: Column Mapping" 
                    icon="pi pi-arrow-left"
                    severity="secondary"
                    onClick={() => setActiveTabIndex(1)}
                  />
                  <Button 
                    label="Next: Upload Options" 
                    icon="pi pi-arrow-right" 
                    iconPos="right"
                    disabled={vendorSelectionMode === 'dropdown' && !selectedVendor}
                    onClick={() => setActiveTabIndex(3)}
                  />
                </div>
              </Card>
            </TabPanel>

            {/* Upload Options Tab */}
            <TabPanel 
              header={
                <div className="flex align-items-center">
                  <i className="pi pi-cloud-upload mr-2"></i>
                  <span>4. Upload Options</span>
                </div>
              }
            >
              <Card title="Database Upload Configuration" className="h-full">
                <div className="grid">
                  <div className="col-12 lg:col-6">
                    <Panel header="Database Targets" className="mb-4">
                                             <div className="field">
                         <div className="flex align-items-center mb-4 p-3 border-round surface-card border-1 surface-border">
                           <Checkbox
                             inputId="uploadProd"
                             checked={uploadToProd}
                             onChange={(e) => setUploadToProd(e.checked!)}
                           />
                           <label htmlFor="uploadProd" className="ml-3 flex-1">
                             <div className="font-semibold text-color">Production Database</div>
                             <div className="text-color-secondary text-sm">Upload to DE-DWH-PROD for live data</div>
                           </label>
                           <i className="pi pi-database text-2xl text-green-400"></i>
                         </div>
                         
                         <div className="flex align-items-center p-3 border-round surface-card border-1 surface-border">
                           <Checkbox
                             inputId="uploadRaw"
                             checked={uploadToRaw}
                             onChange={(e) => setUploadToRaw(e.checked!)}
                           />
                           <label htmlFor="uploadRaw" className="ml-3 flex-1">
                             <div className="font-semibold text-color">Raw Database</div>
                             <div className="text-color-secondary text-sm">Upload to DE-DWH-RAW for staging</div>
                           </label>
                           <i className="pi pi-database text-2xl text-blue-400"></i>
                         </div>
                       </div>
                    </Panel>

                    <div className="text-center">
                                             <Button
                         label="Start Upload Process"
                         icon="pi pi-upload"
                         onClick={handleUpload}
                         disabled={processing || selectedColumns.length === 0 || (!uploadToProd && !uploadToRaw)}
                         className="w-full"
                         style={{ padding: '1rem 2rem', fontSize: '1.1rem' }}
                       />
                    </div>
                  </div>
                  
                  <div className="col-12 lg:col-6">
                    {processing && (
                      <Panel header="Upload Progress" className="mb-4">
                                                 <div className="text-center mb-4">
                           <ProgressBar value={progress} className="mb-3" />
                           <p className="text-color-secondary m-0">{progress}% Complete</p>
                         </div>
                        {renderProgressSteps()}
                      </Panel>
                    )}
                  </div>
                </div>
              </Card>
            </TabPanel>
          </TabView>
        </div>
      </div>

      {/* Unmapped Values Dialog */}
      <Dialog
        header="Resolve Unmapped Values"
        visible={showUnmappedDialog}
        style={{ width: '800px' }}
        onHide={() => setShowUnmappedDialog(false)}
        className="modern-dialog"
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => setShowUnmappedDialog(false)}
              severity="secondary"
            />
            <Button
              label="Submit Changes"
              icon="pi pi-check"
              onClick={handleUnmappedValuesSubmit}
            />
          </div>
        }
      >
        <div className="grid">
          {unmappedValues.map((item, index) => (
            <div key={index} className="col-12">
                             <div className="field p-3 border-round surface-card border-1 surface-border mb-3">
                 <label className="block font-semibold mb-2 text-color">
                   <i className="pi pi-exclamation-triangle text-orange-400 mr-2"></i>
                   Unmapped Value: <strong>{item.value}</strong> 
                   <Badge value={item.table} severity="info" className="ml-2" />
                 </label>
                                 <InputText
                   value={item.standardizedValue}
                   onChange={(e) => {
                     const newValues = [...unmappedValues];
                     newValues[index].standardizedValue = e.target.value;
                     setUnmappedValues(newValues);
                   }}
                   placeholder="Enter standardized value"
                   className="w-full"
                 />
              </div>
            </div>
          ))}
        </div>
      </Dialog>
    </div>
  );
};

export default CatalogUploader;
